import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { APP_TIME_ZONE, getTodayChicagoDateKey } from "./dateFilters";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const DATE_TIME_LOCAL_FORMAT = "YYYY-MM-DDTHH:mm";

export const nowIso = () => new Date().toISOString();

export const formatInAppTimeZone = (value, options = {}) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIME_ZONE,
    ...options,
  }).format(date);
};

export const formatDateTimeInAppTimeZone = (value, fallback = "") => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(APP_TIME_ZONE).format("YYYY-MM-DD HH:mm") : fallback;
};

export const formatDateInAppTimeZone = (value, fallback = "") => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(APP_TIME_ZONE).format("YYYY-MM-DD") : fallback;
};

export const formatLongDateInAppTimeZone = (value, fallback = "") => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(APP_TIME_ZONE).format("YYYY-MM-DD") : fallback;
};

export const formatShortDateInAppTimeZone = (value, fallback = "") => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(APP_TIME_ZONE).format("YYYY-MM-DD") : fallback;
};

export const formatDateKey = (value, options = {}, fallback = "") => {
  if (!value) return fallback;
  const text = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return formatInAppTimeZone(value, options) || fallback;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

export const formatShortDateKey = (value, fallback = "") => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : fallback;
};

export const formatTimeInAppTimeZone = (value, fallback = "") => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(APP_TIME_ZONE).format("HH:mm") : fallback;
};

export const formatTodayWithWeekdayInAppTimeZone = () =>
  formatInAppTimeZone(nowIso(), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export const isoToChicagoDateTimeLocal = (value = nowIso()) => {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "";
  return parsed.tz(APP_TIME_ZONE).format(DATE_TIME_LOCAL_FORMAT);
};

export const chicagoDateTimeLocalToIso = (value) => {
  if (!value) return "";
  const parsed = dayjs.tz(value, DATE_TIME_LOCAL_FORMAT, APP_TIME_ZONE);
  return parsed.isValid() ? parsed.toISOString() : "";
};

export const chicagoDateKeyToStartIso = (dateKey) => {
  if (!dateKey) return "";
  const parsed = dayjs.tz(`${dateKey}T00:00`, DATE_TIME_LOCAL_FORMAT, APP_TIME_ZONE);
  return parsed.isValid() ? parsed.toISOString() : "";
};

export const chicagoDateKeyToEndIso = (dateKey) => {
  if (!dateKey) return "";
  const parsed = dayjs.tz(`${dateKey}T00:00`, DATE_TIME_LOCAL_FORMAT, APP_TIME_ZONE);
  return parsed.isValid() ? parsed.add(1, "day").subtract(1, "millisecond").toISOString() : "";
};

export const todayChicagoDateKey = () => getTodayChicagoDateKey();
